/**
 * BoardSelector - Tab selector for switching between mainboard/sideboard/maybeboard/tokens
 *
 * Shows card counts per board. Hides tabs based on format rules and deck status.
 * Tabs with orphaned cards (cards left in zones that became unavailable after a format
 * or status change) stay visible with a warning indicator so users can move or remove them.
 */
import type { ReactElement } from 'react';

import { useAtom } from 'jotai';
import { GiToken } from 'react-icons/gi';
import { IoWarning } from 'react-icons/io5';

import Tabs from '@/components/Tabs/Tabs';
import { isCommanderFormat } from '@/lib/deckUtils';
import { getSelectableZones } from '@/lib/deckValidation';
import type { DeckCard, DeckFormat, DeckStatus, DeckZone } from '@/types/deck';

import { selectedBoardAtom, type DeckBoard } from '../state/deckEditorSettings';

export interface BoardSelectorProps {
  cards: DeckCard[];
  format: DeckFormat | null;
  status: DeckStatus;
}

interface BoardTab {
  key: DeckBoard;
  label: string;
  zones: DeckZone[];
  icon?: ReactElement;
}

const BOARD_TABS: BoardTab[] = [
  { key: 'main', label: 'Main Deck', zones: ['commander', 'companion', 'main'] },
  { key: 'sideboard', label: 'Sideboard', zones: ['sideboard'] },
  { key: 'maybeboard', label: 'Maybeboard', zones: ['maybeboard'] },
  { key: 'tokens', label: 'Tokens', zones: ['tokens'], icon: <GiToken className="h-3.5 w-3.5" /> },
];

export default function BoardSelector({ cards, format, status }: BoardSelectorProps): ReactElement {
  const [selectedBoard, setSelectedBoard] = useAtom(selectedBoardAtom);

  // Determine which tabs to show based on format and status
  const selectableZones = getSelectableZones(format, status);
  const hasSideboard = selectableZones.includes('sideboard');
  const hasMaybeboard = selectableZones.includes('maybeboard');

  // Check if deck has any token cards
  const hasTokenCards = cards.some((c) => c.zone === 'tokens');

  const visibleTabs = BOARD_TABS.filter((tab) => {
    const hasCardsInZone = cards.some((c) => tab.zones.includes(c.zone));
    if (tab.key === 'sideboard' && !hasSideboard && !hasCardsInZone) return false;
    if (tab.key === 'maybeboard' && !hasMaybeboard && !hasCardsInZone) return false;
    // Only show tokens tab if there are token cards in the deck
    if (tab.key === 'tokens' && !hasTokenCards) return false;
    return true;
  });

  const isCommander = isCommanderFormat(format);

  /** Tab has orphaned cards in zones that are unavailable for the current format/status */
  const hasOrphanedCards = (tab: BoardTab): boolean => {
    if (tab.key === 'sideboard' && !hasSideboard) return true;
    if (tab.key === 'maybeboard' && !hasMaybeboard) return true;
    // Main tab includes commander zone — flag if format doesn't support it but cards exist
    if (tab.key === 'main' && !isCommander && cards.some((c) => c.zone === 'commander')) return true;
    return false;
  };

  // Calculate card counts per board
  const getCardCount = (zones: DeckZone[]): number => {
    return cards.filter((c) => zones.includes(c.zone)).reduce((sum, c) => sum + c.quantity, 0);
  };

  // If current selection is hidden, switch to main
  if (!visibleTabs.some((t) => t.key === selectedBoard)) {
    setSelectedBoard('main');
  }

  return (
    <Tabs
      tabs={visibleTabs.map((tab) => {
        const orphaned = hasOrphanedCards(tab);
        return {
          id: tab.key,
          label: tab.label,
          count: getCardCount(tab.zones),
          icon: orphaned ? <IoWarning className="h-3.5 w-3.5" /> : tab.icon,
          tone: orphaned ? ('warning' as const) : undefined,
          tooltip: orphaned ? 'This zone is not available for the current format — move or remove these cards' : undefined,
        };
      })}
      activeId={selectedBoard}
      onChange={setSelectedBoard}
      variant="pill"
      label="Deck board"
      className="border-storm border"
    />
  );
}
